import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box, 
    Typography, 
    Autocomplete, 
    Button, 
    Grid, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    TextField, 
    TableContainer, 
    Paper, 
    Table, 
    TableHead, 
    TableBody, 
    TableRow, 
    TableCell, 
    Link,
    ThemeProvider,
    Breadcrumbs,
    Card,
    CardContent,
    CardHeader,
    Snackbar,
    Alert,
    CircularProgress
} from '@mui/material';
import { createTheme } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { styled } from "@mui/material/styles";
import EventIcon from '@mui/icons-material/Event';
import { getJobOrders, putJobOrder } from '../API/jobOrderAPI';
import { postBillingRecord } from '../API/billingAPI';
import { getClient, getCacheClients, putClient, putCacheClient } from '../API/clientAPI';
import { getUsers } from '../API/userAPI';
import { format } from 'date-fns';  // Make sure to install date-fns for formatting


const theme = createTheme({
    palette: {
      primary: {
        main: "#1e5d45", // Custom primary color
      },
    },
  });
  
const StyledHr = styled("hr")(({ theme }) => ({
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: theme.spacing(4, 0),
    opacity: 0.8,
}));


function CreateBillingRecord() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [clientsCache, setClientsCache] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [client, setClient] = useState(null);
    const [users, setUsers] = useState([])
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const [billingRecord, setBillingRecord] = useState(
        {
            billing_invoice_number: '',
            client: '',
            billing_date: '',
            due_date: '',
            payment_date: '',
            jobOrders: [],
            total_amount: '',
            net_amount: '',
            tax_amount: '',
            prepared_by: '',
            verified_by: '',
            payment_status: 'Not Billed'
        }
    )

    useEffect(() => {
        const fetchCacheClientsAndUsers = async () => {
            try {
                // Fetch clients
                const clientsCache = await getCacheClients(); // Pass status if needed
                setClientsCache(clientsCache);

                // Fetch users
                const users = await getUsers();
                setUsers(users);  // Assuming setUsers is defined for storing users

                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        // Call the combined fetch function
        fetchCacheClientsAndUsers();
    }, []);


    const handleSearchButtonClick = async () => {
        if (!selectedClient) {
            console.log("No client selected");
            return;
        }

        setIsLoading(true);

        try {
            const fetchedClient = await getClient(selectedClient.id);
            setClient(fetchedClient)

            // Filter for billing addresses and contacts without remapping
            const billingAddresses = fetchedClient.addresses.filter(address => address.billing === true);
            const billingContacts = fetchedClient.contacts.filter(contact => contact.billing === true);

            // Construct the client object in the same format as the fetched data
            const clientData = {
                addresses: billingAddresses,
                timestamp_update: fetchedClient.timestamp_update,
                timestamp_create: fetchedClient.timestamp_create,
                user_update: fetchedClient.user_update,
                name: fetchedClient.name,
                id: fetchedClient.id,
                contacts: billingContacts,
                user_create: fetchedClient.user_create
            };

            // Fetch job orders with 'Not Done' billing record status
            const jobOrders = await getJobOrders([], [], ['Not Done'], '', '', selectedClient.id);

            // Transform job orders to extract only required fields
            const transformedJobOrders = jobOrders.map((jobOrder) => {
                // Format job_date to "MMM DD, YYYY"
                const formattedDate = format(new Date(jobOrder.job_date), "MMM dd, yyyy");

                // Generate jobScope based on competencies and specialization
                const jobScope = jobOrder.competencies
                    ?.filter((competency) => competency !== "driving_2_wheels" && competency !== "driving_4_wheels")
                    .map((competency) => {
                        // Format competency name
                        const formattedCompetency = competency
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (char) => char.toUpperCase());

                        // Determine specialization key
                        const specializationKey = competency === "general_pest_control" ? "general_pest_control" : "termite";

                        // Format specialization words
                        const formattedSpecialization = (jobOrder.specialization?.[specializationKey] || []).map((word) =>
                            word.length === 2
                                ? word.toUpperCase()
                                : word.charAt(0).toUpperCase() + word.slice(1)
                        );

                        return {
                            competency: formattedCompetency,
                            specialization: formattedSpecialization,
                        };
                    }) || [];

                // Return limited transformed job order
                return {
                    id: jobOrder.id,                          // Keep ID
                    job_date: formattedDate,                  // Transformed job_date
                    jobScope: jobScope,                       // Transformed jobScope
                    amount: jobOrder.service_fee       // Assign service_fee to amount, default to 0 if undefined
                };
            });

            // Calculate amounts using the transformed job orders
            const calculatedAmounts = calculateAmounts(transformedJobOrders);

            // Update billing record with client, transformed job orders, and calculated amounts
            setBillingRecord(prevRecord => ({
                ...prevRecord,
                client: clientData,
                jobOrders: transformedJobOrders,
                ...calculatedAmounts  // Populate total_amount, net_amount, and tax_amount
            }));

        } catch (error) {
            console.error("Error fetching client details:", error);
        } finally {
            setIsLoading(false);
        }
    };



    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };

    const handleClientChange = (client) => {



        setBillingRecord((prev) => ({
            ...prev,
            client,
            total_amount: '',
            net_amount: '',
            tax_amount: '',
        }));
    };




    const handleBillingRecordChange = (field, value) => {
        setBillingRecord((prev) => {
            const updatedRecord = {
                ...prev,
                [field]: value
            };

            if (field === 'due_date') {
                updatedRecord.payment_status = value !== '' ? 'Billed' : 'Not Billed';
            }

            return updatedRecord;
        });
    };



    // Helper function to calculate amounts
    const calculateAmounts = (jobOrders) => {
        const totalAmount = jobOrders.reduce(
            (sum, jobOrder) => sum + (parseFloat(jobOrder.amount) || 0),
            0
        );

        const netAmount = (totalAmount / 1.12).toFixed(2);
        const vatValue = parseFloat((netAmount * 0.12).toFixed(2));

        return {
            total_amount: totalAmount.toFixed(2),
            net_amount: netAmount,
            tax_amount: vatValue
        };
    };

    // Handle amount change
    const handleAmountChange = (id, value) => {
        setBillingRecord((prev) => {
            const updatedJobOrders = prev.jobOrders.map((jobOrder) =>
                jobOrder.id === id
                    ? { ...jobOrder, amount: value }
                    : jobOrder
            );

            return {
                ...prev,
                jobOrders: updatedJobOrders,
                ...calculateAmounts(updatedJobOrders)  // Reuse calculation logic
            };
        });
    };

    // Handle job order removal
    const handleRemove = (indexToRemove) => {
        setBillingRecord((prev) => {
            const updatedJobOrders = prev.jobOrders.filter(
                (_, index) => index !== indexToRemove
            );

            return {
                ...prev,
                jobOrders: updatedJobOrders,
                ...calculateAmounts(updatedJobOrders)  // Reuse calculation logic
            };
        });
    };



    const handleSearchClient = (event, value) => {
        setSelectedClient(value || null); // Update the selected client
    };

    async function handleSubmit(e) {
        e.preventDefault();
        setIsLoading(true);  // Set loading to true

        // Transform billingRecord before submission
        const transformedBillingRecord = {
            ...billingRecord,
            client_id: billingRecord.client.id,  // Replace client with client_id
            job_orders: billingRecord.jobOrders.map(order => order.id),  // Extract only jobOrder IDs
        };

        // Remove the original client and jobOrders keys to avoid redundancy
        delete transformedBillingRecord.client;
        delete transformedBillingRecord.jobOrders;

        console.log(transformedBillingRecord);

        // Determine billing_record_status based on payment_status
        const billing_record_status = billingRecord.payment_status === 'Not Billed'
            ? 'Not Done'
            : 'Done';

        try {
            // Loop through job orders and update their billing_record_status and service_fee
            await Promise.all(
                billingRecord.jobOrders.map(async (order) => {
                    const payload = {
                        billing_record_status,
                        service_fee: order.amount
                    };
                    await putJobOrder(order.id, payload);  // Update each job order
                })
            );

            await postBillingRecord(transformedBillingRecord);  // Submit transformed data to create billing record
            console.log('Billing record and job orders updated successfully');
        } catch (error) {
            console.error('Error updating job orders or billing record:', error);
        } finally {
            setIsLoading(false);  // Stop loading
        }
    }


    return (
        <ThemeProvider theme={theme}>
            {/* Success Snackbar */}
            <Snackbar
            open={!!successMessage}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            onClose={() => setSuccessMessage("")}
            >
            <Alert
                severity="success"
                sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "auto",
                maxWidth: "100%",
                mx: "auto",
                }}
            >
                {successMessage}
            </Alert>
            </Snackbar>
    
            {/* Loading Snackbar */}
            <Snackbar
            open={isLoading}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
            <Alert
                severity="info"
                icon={<CircularProgress size={18} />}
                sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "auto",
                maxWidth: "100%",
                mx: "auto",
                }}
            >
                Loading, please wait...
            </Alert>
            </Snackbar>
    
            {/* Error Snackbar */}
            <Snackbar
            open={!!errorMessage}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            onClose={() => setErrorMessage("")}
            >
            <Alert
                severity="error"
                sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "auto",
                maxWidth: "100%",
                mx: "auto",
                }}
            >
                {errorMessage}
            </Alert>
            </Snackbar>
    
            <Box sx={{ padding: "40px 60px" }}>
                <Card
                    variant="outlined"
                    style={{
                        marginTop: "40px",
                        marginBottom: "40px",
                        borderRadius: 0,
                    }}
                >
                    <CardHeader
                        title={
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box display="flex" alignItems="center">
                            <EventIcon
                                sx={{
                                fontSize: "40px",
                                marginRight: "8px",
                                }}
                            />
                            <Typography variant="h6">Finance</Typography>
                            </Box>
                            <Breadcrumbs
                            aria-label="breadcrumb"
                            separator={<NavigateNextIcon fontSize="small" />}
                            sx={{ color: "white" }}
                            >
                            <Typography sx={{ color: "#ffffff99" }}>
                                Finance
                            </Typography>
                            <Typography
                                component={RouterLink}
                                to="/manage-billing-records"
                                sx={{ color: "#ffffff99", textDecoration: "none" }}
                            >
                                Manage Billing Records
                            </Typography>
                            <Typography sx={{ color: "#ffffff", textDecoration: "none" }}>
                                Create Billing Record
                            </Typography>
                            </Breadcrumbs>
                        </Box>
                        }
                        sx={{
                        backgroundColor: "#1e5d45",
                        color: "white",
                        padding: "16px",
                        }}
                    />
                    <CardContent sx={{ padding: "20px" }}>
                        <Card variant="outlined" sx={{ padding: "20px" }}>
                            <CardHeader
                                title={
                                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                                    Create Billing Record
                                    </Typography>
                                }
                                sx={{ marginBottom: 2 }}
                            />
                            <form onSubmit={handleSubmit} className="billing-record-form">
                                <Grid container spacing={3}>

                                    {/* Client Information Section */}
                                    <Grid
                                        item
                                        xs={12}
                                        md={12}
                                        sx={{ display: "flex", flexDirection: "column" }}
                                    >
                                        <Card
                                            variant="outlined"
                                            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                                        >
                                            <CardHeader
                                                title={
                                                    <Typography
                                                    variant="h6"
                                                    sx={{
                                                        paddingBottom: "1rem",
                                                        fontWeight: "bold",
                                                        textTransform: "uppercase",
                                                        fontSize: "1rem",
                                                    }}
                                                    >
                                                    Client Information
                                                    </Typography>
                                                }
                                            />
                                            <CardContent
                                                sx={{
                                                flex: 1,
                                                display: "flex",
                                                flexDirection: "column",
                                                }}
                                            >
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12}>
                                                        <Grid container spacing={3} >
                                                            <Grid item xs={4}>
                                                                <Autocomplete
                                                                    freeSolo
                                                                    options={clientsCache}
                                                                    getOptionLabel={(client) => client?.name || ""} // Fallback to an empty string
                                                                    value={selectedClient} // Controlled component
                                                                    onChange={(event, newValue) =>
                                                                        handleSearchClient(event, newValue)
                                                                    } // Updates selected value
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            label="Client Name"
                                                                            variant="outlined"
                                                                            fullWidth
                                                                            error={!!error} // Displays error styling if there is an error
                                                                            helperText={error || ""} // Displays error message
                                                                        />
                                                                    )}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={4} container alignItems="center">
                                                                <Button
                                                                    variant="contained"
                                                                    color="primary"
                                                                    onClick={handleSearchButtonClick} // Call button handler
                                                                    disabled={isLoading} // Disable button while loading
                                                                >
                                                                    {isLoading ? "Searching..." : "Search"}{" "}
                                                                    {/* Change button label dynamically */}
                                                                </Button>

                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            fullWidth
                                                            label="Category"
                                                            variant="outlined"
                                                            value={client?.category || ''}
                                                            disabled
                                                        />
                                                    </Grid>

                                                    <Grid item xs={4}>
                                                        <TextField
                                                            fullWidth
                                                            label="Parent Client"
                                                            variant="outlined"
                                                            value={client?.parent_client?.name || ''}
                                                            disabled
                                                        />
                                                    </Grid>
                                                    
                                                    <Grid item xs={12}>
                                                        <Grid container spacing={3}>
                                                            <Grid item xs={8}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Billing Address"
                                                                    value={
                                                                        billingRecord?.client?.addresses?.[0]?.house_unit_street
                                                                            ? `${billingRecord.client.addresses[0].house_unit_street}, ${billingRecord.client.addresses[0].barangay}, ${billingRecord.client.addresses[0].city}`
                                                                            : ''
                                                                    } // Directly access the first address
                                                                    disabled
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <Grid container spacing={3} >
                                                            <Grid item xs={4}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Billing Contact"
                                                                    value={
                                                                        billingRecord?.client?.contacts?.[0]
                                                                            ? `${billingRecord.client.contacts[0].firstname} ${billingRecord.client.contacts[0].lastname}`
                                                                            : ''
                                                                    } // Directly access the first contact
                                                                    disabled
                                                                />
                                                            </Grid>

                                                            <Grid item xs={4}>
                                                                {
                                                                    billingRecord?.client?.contacts?.[0]?.numbers?.length > 0
                                                                        ? billingRecord.client.contacts[0].numbers.map((number, index) => (
                                                                            <TextField
                                                                                key={index}
                                                                                fullWidth
                                                                                label="Billing Number"
                                                                                value={`${number.type}: ${number.number}`}
                                                                                disabled
                                                                                sx={{ marginBottom: 2 }}
                                                                            />
                                                                        ))
                                                                        : (
                                                                            <TextField
                                                                                fullWidth
                                                                                label="Billing Number"
                                                                                value=""
                                                                                disabled
                                                                            />
                                                                        )
                                                                }
                                                            </Grid>

                                                            <Grid item xs={4}>
                                                                {
                                                                    billingRecord?.client?.contacts?.[0]?.emails?.length > 0
                                                                        ? billingRecord.client.contacts[0].emails.map((email, index) => (
                                                                            <TextField
                                                                                key={index}
                                                                                fullWidth
                                                                                label="Billing Email"
                                                                                value={`${email.email}`}
                                                                                disabled
                                                                                sx={{ marginBottom: 2 }}
                                                                            />
                                                                        ))
                                                                        : (
                                                                            <TextField
                                                                                fullWidth
                                                                                label="Billing Email"
                                                                                value=""
                                                                                disabled
                                                                            />
                                                                        )
                                                                }
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                                        
                                    {/* Billing Information Section */}
                                    <Grid
                                        item
                                        xs={12}
                                        md={12}
                                        sx={{ display: "flex", flexDirection: "column" }}
                                    >
                                        <Card
                                            variant="outlined"
                                            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                                        >
                                            <CardHeader
                                                title={
                                                    <Typography
                                                    variant="h6"
                                                    sx={{
                                                        paddingBottom: "1rem",
                                                        fontWeight: "bold",
                                                        textTransform: "uppercase",
                                                        fontSize: "1rem",
                                                    }}
                                                    >
                                                    Billing Information
                                                    </Typography>
                                                }
                                            />
                                            <CardContent
                                                sx={{
                                                flex: 1,
                                                display: "flex",
                                                flexDirection: "column",
                                                }}
                                            >
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12}>
                                                        <Grid container spacing={3}>
                                                            <Grid item xs={4}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Billing Invoice Number"
                                                                    value={
                                                                        billingRecord.billing_invoice_number
                                                                    } // Safely handle undefined cases and provide a default empty string
                                                                    onChange={(e) => handleBillingRecordChange('billing_invoice_number', e.target.value)}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={2}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Billing Date"
                                                                    type="date" // Set the type to date
                                                                    value={billingRecord.billing_date || ''} // Ensure value is not undefined
                                                                    onChange={(e) => handleBillingRecordChange('billing_date', e.target.value)}
                                                                    InputLabelProps={{
                                                                        shrink: true, 
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={2}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Due Date"
                                                                    type="date" // Set the type to date
                                                                    value={billingRecord.due_date || ''} // Ensure value is not undefined
                                                                    onChange={(e) => handleBillingRecordChange('due_date', e.target.value)}
                                                                    InputLabelProps={{
                                                                        shrink: true,
                                                                    }}
                                                                />
                                                            </Grid>                                                            
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <FormControl fullWidth>
                                                            <InputLabel id="prepared-by-label" >
                                                                Prepared By
                                                            </InputLabel>
                                                            <Select
                                                                labelId="prepared-by-label"
                                                                value={billingRecord.prepared_by}
                                                                onChange={(e) => handleBillingRecordChange('prepared_by', e.target.value)}
                                                                label="Prepared By"
                                                            >
                                                                {users
                                                                    .filter(user => user.roles.includes('finance'))  // Filter users with 'finance' role
                                                                    .map(user => (
                                                                        <MenuItem
                                                                            key={user.id}
                                                                            value={`${user.firstname} ${user.lastname}`}
                                                                        >
                                                                            {user.firstname} {user.lastname}
                                                                        </MenuItem>
                                                                    ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <FormControl fullWidth>
                                                            <InputLabel id="verified-by-label" >Verified By</InputLabel>
                                                            <Select
                                                                labelId="verified-by-label"
                                                                value={billingRecord.verified_by}
                                                                onChange={(e) => handleBillingRecordChange('verified_by', e.target.value)}
                                                                label="Verified By"
                                                            >
                                                                <MenuItem value="EJ Marinay">EJ Marinay</MenuItem>
                                                                <MenuItem value="April Marinay">April Marinay</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Grid container spacing={3}>
                                                            <Grid item xs={4}>
                                                                <FormControl fullWidth>
                                                                    <InputLabel id="payment-status-label" >Payment Status</InputLabel>
                                                                    <Select
                                                                        labelId="payment-status-label"
                                                                        value={billingRecord.payment_status}
                                                                        onChange={(e) => handleBillingRecordChange('payment_status', e.target.value)}
                                                                        label="Payment Status"
                                                                    >
                                                                        <MenuItem value="Not Billed">Not Billed</MenuItem>
                                                                        <MenuItem value="Billed">Billed</MenuItem>
                                                                        <MenuItem value="Paid">Paid</MenuItem>
                                                                        <MenuItem value="Not Due">Not Due</MenuItem>
                                                                        <MenuItem value="30 days overdue">30 days overdue</MenuItem>
                                                                        <MenuItem value="60 days overdue">60 days overdue</MenuItem>
                                                                        <MenuItem value="90 days overdue">90 days overdue</MenuItem>
                                                                        <MenuItem value="120 days overdue">120 days overdue</MenuItem>
                                                                        <MenuItem value="150 days overdue">150 days overdue</MenuItem>
                                                                        <MenuItem value="180 days overdue">180 days overdue</MenuItem>
                                                                        <MenuItem value=">180 days overdue">&gt;180 days overdue</MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={2}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Payment Date"
                                                                    type="date" // Set the type to date
                                                                    value={billingRecord.payment_date || ''} // Ensure value is not undefined
                                                                    onChange={(e) => handleBillingRecordChange('payment_date', e.target.value)}
                                                                    InputLabelProps={{
                                                                        shrink: true,                                                               
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={4}>
                                                                <FormControl fullWidth>
                                                                    <InputLabel id="collected-by-label">Collected By</InputLabel>
                                                                    <Select
                                                                        labelId="collected-by-label"
                                                                        value={billingRecord.collected_by}
                                                                        onChange={(e) => handleBillingRecordChange('collected_by', e.target.value)}
                                                                        label="Collected By"
                                                                    >
                                                                        {users
                                                                            .filter(user => user.roles.includes('finance'))  // Filter users with 'finance' role
                                                                            .map(user => (
                                                                                <MenuItem
                                                                                    key={user.id}
                                                                                    value={`${user.firstname} ${user.lastname}`}
                                                                                >
                                                                                    {user.firstname} {user.lastname}
                                                                                </MenuItem>
                                                                            ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* Cost Breakdown Section */}
                                    <Grid
                                        item
                                        xs={12}
                                        md={12}
                                        sx={{ display: "flex", flexDirection: "column" }}
                                    >
                                        <Card
                                            variant="outlined"
                                            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
                                        >
                                            <CardHeader
                                                title={
                                                    <Typography
                                                    variant="h6"
                                                    sx={{
                                                        paddingBottom: "1rem",
                                                        fontWeight: "bold",
                                                        textTransform: "uppercase",
                                                        fontSize: "1rem",
                                                    }}
                                                    >
                                                    Cost Breakdown
                                                    </Typography>
                                                }
                                            />
                                            <CardContent
                                                sx={{
                                                flex: 1,
                                                display: "flex",
                                                flexDirection: "column",
                                                }}
                                            >
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12}>
                                                        <TableContainer component={Paper}>
                                                            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
                                                                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                                                                    <TableRow>
                                                                        <TableCell style={{ width: '25%' }} sx={{ fontWeight: "bold" }}>Date</TableCell>
                                                                        <TableCell style={{ width: '40%' }} sx={{ fontWeight: "bold" }}>Job Scope</TableCell>
                                                                        <TableCell style={{ width: '25%' }} sx={{ fontWeight: "bold" }}>Amount</TableCell>
                                                                        <TableCell style={{ width: '10%' }}></TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody sx={{ backgroundColor: "#fbfbfb" }}>
                                                                    {billingRecord.jobOrders.map((jobOrder, index) => (
                                                                        <TableRow key={index}>
                                                                            <TableCell>{jobOrder.job_date}</TableCell>
                                                                            <TableCell>
                                                                                <Typography variant="body2">
                                                                                    <b>Job Order ID:</b>{' '}
                                                                                    <Link
                                                                                        href={`/view-job-order/${jobOrder.id}`}
                                                                                        target="_blank"  // Opens the link in a new tab
                                                                                        rel="noopener noreferrer"  // Security best practice to prevent access to window.opener
                                                                                        style={{ textDecoration: 'none', color: '#1976d2' }}  // Styling for the link
                                                                                    >
                                                                                        {jobOrder.id}
                                                                                    </Link>
                                                                                </Typography>


                                                                                {jobOrder.jobScope.length > 0 ? (
                                                                                    <ul style={{ margin: 0, padding: '0 0 0 16px', listStyleType: 'disc' }}>
                                                                                        {jobOrder.jobScope.map((scope, scopeIndex) => (
                                                                                            <li key={scopeIndex}>
                                                                                                <Typography variant="body2">
                                                                                                    <b>{scope.competency}</b>
                                                                                                    {scope.specialization.length > 0
                                                                                                        ? `: ${scope.specialization.join(', ')}`
                                                                                                        : ''}
                                                                                                </Typography>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                ) : (
                                                                                    <Typography variant="body2">No specific scope defined.</Typography>
                                                                                )}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <TextField
                                                                                    fullWidth
                                                                                    value={jobOrder.amount} // Ensure value is not undefined
                                                                                    onChange={(e) => handleAmountChange(jobOrder.id, e.target.value)}
                                                                                    InputLabelProps={{
                                                                                        shrink: true, // Ensures the label doesn't overlap with the input
                                                                                        style: { fontSize: '0.875rem' }, // Styling for the label
                                                                                    }}
                                                                                    inputProps={{
                                                                                        style: { fontSize: '0.875rem' }, // Styling for the date field content
                                                                                    }}
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell style={{ textAlign: 'center' }}>
                                                                                <Button
                                                                                    variant="outlined"
                                                                                    color="secondary"
                                                                                    sx={{
                                                                                        fontSize: "0.875rem",
                                                                                        color: "#FF4C4C",
                                                                                        borderColor: "#FF4C4C",
                                                                                    }}
                                                                                    onClick={() => handleRemove(index)}
                                                                                    disabled={billingRecord.jobOrders.length === 1} // Disable button when there's only one row
                                                                                >
                                                                                    Remove
                                                                                </Button>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}  
                                                                    <TableRow>
                                                                        <TableCell/>
                                                                        <TableCell/>
                                                                        <TableCell/>
                                                                        <TableCell/>                                                                
                                                                    </TableRow>                                                                    
                                                                </TableBody>
                                                            </Table>
                                                            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
                                                                <TableBody sx={{ backgroundColor: "#fbfbfb" }}>
                                                                    <TableRow>
                                                                        <TableCell rowSpan={3} colSpan={4}/>                                                              
                                                                        <TableCell sx={{ fontWeight: "bold" }}>Net Amount</TableCell>
                                                                        <TableCell align="right">{billingRecord.net_amount}</TableCell>
                                                                        <TableCell/>
                                                                    </TableRow>
                                                                    <TableRow>
                                                                        <TableCell sx={{ fontWeight: "bold" }}>VAT @ 12%</TableCell>
                                                                        <TableCell align="right">{billingRecord.tax_amount}</TableCell>
                                                                        <TableCell/>                     
                                                                    </TableRow>
                                                                    <TableRow> 
                                                                        <TableCell sx={{ fontWeight: "bold" }}>Total Amount</TableCell>
                                                                        <TableCell align="right">{billingRecord.total_amount}</TableCell>
                                                                        <TableCell/>                    
                                                                    </TableRow>
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>

                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    {/* Form buttons */}
                                    <Grid
                                        item
                                        xs={12}
                                        display="flex"
                                        justifyContent="flex-end"
                                        alignItems="center"
                                        mt={2}
                                        mb={2}
                                        mr={2}
                                        >
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            onClick={() => navigate('/manage-billing-records')}
                                            sx={{
                                                fontSize: "0.875rem",
                                                color: "#FF4C4C",
                                                borderColor: "#FF4C4C",
                                            }}
                                            disabled={isLoading}
                                        >
                                            Cancel
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            color="primary" 
                                            type="submit" 
                                            sx={{
                                                marginLeft: "10px",
                                                fontSize: "0.875rem",
                                                }}
                                            disabled={isLoading}
                                        >
                                            Submit
                                        </Button>                                  
                                    </Grid>

                                </Grid>
                            </form>
                        </Card>
                    </CardContent>
                </Card>
            </Box>
        </ThemeProvider>
    );
}

export default CreateBillingRecord;
